import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import apiClient from "../api/client";
import "./StravaSyncModal.css";
import { useSyncStatus } from "../contexts/SyncStatusContext";

const AUTO_SYNC_JOB_KEY = "stravaAutoSyncJobId";

const TABS = {
  sync: "Sync",
  connection: "Connection",
};

const INITIAL_SYNC_STATE = {
  syncing: false,
  fullResyncing: false,
  progress: null,
  message: "",
  error: "",
};

const StravaSyncModal = ({
  isOpen,
  initialTab = "sync",
  onRequestClose,
  onStatusUpdate,
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [statusLoading, setStatusLoading] = useState(false);
  const [stravaConnected, setStravaConnected] = useState(false);
  const [stravaAthlete, setStravaAthlete] = useState(null);
  const [syncState, setSyncState] = useState(INITIAL_SYNC_STATE);
  const [authorizeLoading, setAuthorizeLoading] = useState(false);
  const [connectionMessage, setConnectionMessage] = useState("");
  const [connectionError, setConnectionError] = useState("");
  const pollIntervalRef = useRef(null);
  const { startSync, completeSync, errorSync } = useSyncStatus();

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const fetchStravaStatus = useCallback(async () => {
    if (!user?.email) {
      return;
    }
    setStatusLoading(true);
    try {
      const response = await apiClient.get('/strava/status');
      const connected = Boolean(response.data.connected);
      const athlete = response.data.athlete || null;
      const syncedActivities = response.data.synced_activities || 0;
      const lastSync = response.data.last_sync || null;
      setStravaConnected(connected);
      setStravaAthlete(athlete);
      onStatusUpdate?.({ connected, athlete, syncedActivities, lastSync });
    } catch (error) {
      console.error("Failed to fetch Strava status:", error);
    } finally {
      setStatusLoading(false);
    }
  }, [user, onStatusUpdate]);

  const clearPoll = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
      setSyncState(INITIAL_SYNC_STATE);
      setConnectionMessage("");
      setConnectionError("");
      fetchStravaStatus();
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
      clearPoll();
    }

    return () => {
      document.body.classList.remove("no-scroll");
      clearPoll();
    };
  }, [clearPoll, fetchStravaStatus, initialTab, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onRequestClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onRequestClose]);

  const handleAuthorizeRedirect = async () => {
    if (!user?.email) return;
    setAuthorizeLoading(true);
    try {
      const redirectUri = `${window.location.origin}/strava-callback`;
      const response = await apiClient.get('/strava/authorize', {
        params: { redirect_uri: redirectUri },
      });
      const authorizeUrl = response.data?.authorize_url;
      if (authorizeUrl) {
        window.location.href = authorizeUrl;
      } else {
        throw new Error("Missing authorize URL");
      }
    } catch (error) {
      console.error("Failed to start Strava authorization:", error);
      setSyncState((prev) => ({
        ...prev,
        error:
          "Unable to open Strava authorization. Please refresh and try again.",
      }));
    } finally {
      setAuthorizeLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!user?.email || !stravaConnected) return;
    const confirmed = window.confirm(
      "Disconnect Strava? Automatic syncing will stop, but existing activities stay in your dashboard."
    );
    if (!confirmed) return;
    setAuthorizeLoading(true);
    setConnectionMessage("");
    setConnectionError("");
    try {
      await apiClient.post('/strava/disconnect');
      setConnectionMessage("Strava disconnected. Reconnect anytime to resume syncing.");
      fetchStravaStatus();
    } catch (error) {
      console.error("Failed to disconnect Strava:", error);
      const message =
        error.response?.data?.error ||
        error.message ||
        "Failed to disconnect. Please try again.";
      setConnectionError(message);
    } finally {
      setAuthorizeLoading(false);
    }
  };

  const startPoll = useCallback(
    (jobId, successMessage, options = {}) => {
      const { clearAutoJobKey = false } = options;
      clearPoll();
      pollIntervalRef.current = setInterval(async () => {
        try {
          const statusResponse = await apiClient.get(
            `/strava/sync/status/${jobId}`
          );
          const { status, progress, result, error } = statusResponse.data;

          if (status === "finished") {
            clearPoll();
            if (clearAutoJobKey) {
              localStorage.removeItem(AUTO_SYNC_JOB_KEY);
            }
            const syncedCount = result?.synced ?? 0;
            const finalMessage =
              successMessage ||
              `Successfully synced ${syncedCount} new activit${
                syncedCount === 1 ? "y" : "ies"
              }!`;
            setSyncState({
              syncing: false,
              fullResyncing: false,
              progress: 100,
              message: finalMessage,
              error: "",
            });
            completeSync(finalMessage);
            fetchStravaStatus();
          } else if (status === "failed") {
            clearPoll();
            if (clearAutoJobKey) {
              localStorage.removeItem(AUTO_SYNC_JOB_KEY);
            }
            const errorMessage = error || "Sync failed. Please try again.";
            setSyncState({
              syncing: false,
              fullResyncing: false,
              progress: null,
              message: "",
              error: errorMessage,
            });
            errorSync(errorMessage);
          } else {
            setSyncState((prev) => ({
              ...prev,
              progress:
                typeof progress === "number"
                  ? Math.max(progress, prev.progress ?? 5)
                  : prev.progress ?? 5,
              message: "Sync in progress…",
            }));
          }
        } catch (pollError) {
          console.error("Error polling sync status:", pollError);
        }
      }, 2000);
    },
    [clearPoll, completeSync, errorSync, fetchStravaStatus]
  );

  useEffect(() => {
    if (!isOpen) return undefined;
    const autoJobId = localStorage.getItem(AUTO_SYNC_JOB_KEY);
    if (!autoJobId) return undefined;

    setSyncState({
      syncing: false,
      fullResyncing: true,
      progress: null,
      message: "Initial Strava sync in progress…",
      error: "",
    });
    startSync("Syncing activities from Strava…");
    startPoll(autoJobId, "Initial Strava sync completed successfully.", {
      clearAutoJobKey: true,
    });
    return undefined;
  }, [isOpen, startPoll, startSync]);

  const handleSyncStart = async ({ endpoint, successMessage, days }) => {
    if (!user?.email) return;
    setSyncState({
      syncing: endpoint === "/strava/sync",
      fullResyncing: endpoint !== "/strava/sync",
      progress: null,
      message: "Starting sync…",
      error: "",
    });
    startSync("Syncing activities from Strava…");

    try {
      const payload = {};
      if (days !== undefined) {
        payload.days = days;
      }
      const response = await apiClient.post(endpoint, payload);

      const jobId = response.data?.job_id;
      if (!jobId) {
        const fallbackMessage =
          response.data?.message || "Sync completed successfully.";
        setSyncState({
          syncing: false,
          fullResyncing: false,
          progress: 100,
          message: fallbackMessage,
          error: "",
        });
        completeSync(fallbackMessage);
        fetchStravaStatus();
        return;
      }

      setSyncState((prev) => ({
        ...prev,
        progress: 5,
        message: "Sync started…",
      }));
      startPoll(jobId, successMessage);
    } catch (error) {
      console.error("Failed to start Strava sync:", error);
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Unable to start sync. Please try again.";
      setSyncState({
        syncing: false,
        fullResyncing: false,
        progress: null,
        message: "",
        error: errorMessage,
      });
      errorSync(errorMessage);
    }
  };

  const handleSync = () =>
    handleSyncStart({
      endpoint: "/strava/sync",
      successMessage: null,
    });

  const handleFullResync = () => {
    const confirmed = window.confirm(
      "This will sync your entire Strava history (all years). It may take several minutes and counts toward your Strava API limits. Continue?"
    );
    if (!confirmed) return;

    handleSyncStart({
      endpoint: "/strava/sync/backfill",
      days: null, // Request full history
      successMessage: "Full history re-sync completed successfully.",
    });
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="strava-modal-backdrop"
      role="dialog"
      aria-modal="true"
      onClick={onRequestClose}
    >
      <div
        className="strava-modal-card"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="strava-modal-header">
          <div>
            <h2>Strava Integration</h2>
            <p>Connect and sync your runs without leaving the dashboard.</p>
          </div>
          <button
            type="button"
            className="strava-close-button"
            onClick={onRequestClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="strava-modal-tabs">
          {Object.entries(TABS).map(([key, label]) => (
            <button
              key={key}
              className={`strava-tab ${activeTab === key ? "active" : ""}`}
              onClick={() => setActiveTab(key)}
              type="button"
            >
              {label}
            </button>
          ))}
        </div>

        <div className="strava-modal-body">
          {activeTab === "connection" && (
            <div className="strava-section">
              {statusLoading ? (
                <div className="strava-loading">Checking Strava status…</div>
              ) : (
                <>
              <div className="strava-summary">
                <span
                  className={`status-pill ${
                    stravaConnected ? "connected" : "disconnected"
                  }`}
                >
                  {stravaConnected ? "Connected" : "Not connected"}
                </span>
                {stravaConnected && stravaAthlete ? (
                  <p>
                    Linked as{" "}
                    <strong>
                      {stravaAthlete.firstname} {stravaAthlete.lastname}
                    </strong>
                  </p>
                ) : (
                  <p>Connect your Strava account to keep activities synced.</p>
                )}
              </div>

              <div className="strava-actions vertical">
                <button
                  type="button"
                  className="primary-button"
                  onClick={handleAuthorizeRedirect}
                  disabled={authorizeLoading}
                >
                  {authorizeLoading
                    ? "Opening Strava…"
                    : stravaConnected
                    ? "Manage Strava Connection"
                    : "Connect Strava"}
                </button>
                {stravaConnected && (
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={handleDisconnect}
                    disabled={authorizeLoading}
                  >
                    Disconnect Strava
                  </button>
                )}
              </div>
              {connectionMessage && (
                <div className="strava-feedback success">{connectionMessage}</div>
              )}
              {connectionError && (
                <div className="strava-feedback error">{connectionError}</div>
              )}
            </>
          )}
        </div>
          )}

          {activeTab === "sync" && (
            <div className="strava-section">
              <div className="strava-summary">
                <span
                  className={`status-pill ${
                    stravaConnected ? "connected" : "disconnected"
                  }`}
                >
                  {stravaConnected ? "Auto-sync ready" : "Connection required"}
                </span>
                <p>
                  {stravaConnected
                    ? "Webhooks deliver new activities automatically. Use these controls to refresh or recover missed runs."
                    : "Connect Strava first to enable syncing. New activities will auto-sync once linked."}
                </p>
              </div>

              <div className="strava-actions">
                <button
                  type="button"
                  className="primary-button"
                  onClick={handleSync}
                  disabled={
                    !stravaConnected || syncState.syncing || syncState.fullResyncing
                  }
                >
                  {syncState.syncing ? "Syncing…" : "Sync latest activities"}
                </button>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={handleFullResync}
                  disabled={
                    !stravaConnected || syncState.syncing || syncState.fullResyncing
                  }
                >
                  {syncState.fullResyncing ? "Re-syncing…" : "Full re-sync"}
                </button>
              </div>

              {(syncState.syncing || syncState.fullResyncing) && (
                <div className="strava-progress">
                  {typeof syncState.progress === "number" ? (
                    <div className="progress-bar">
                      <div
                        className="progress-bar-fill"
                        style={{ width: `${syncState.progress}%` }}
                      >
                        {syncState.progress}%
                      </div>
                    </div>
                  ) : (
                    <div className="spinner" aria-hidden="true" />
                  )}
                  <p>{syncState.message || "Sync in progress…"}</p>
                  {syncState.fullResyncing && (
                    <p className="sync-hint">You can close this window.</p>
                  )}
                </div>
              )}

              {syncState.message && !syncState.syncing && !syncState.fullResyncing && (
                <div className="strava-feedback success">{syncState.message}</div>
              )}
              {syncState.error && (
                <div className="strava-feedback error">{syncState.error}</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StravaSyncModal;
